import { useState, useMemo } from "react";
import type { WorldDocument } from "@private-fiction/story-core";
import { AccountMediaImage } from "./AccountMedia";

export interface SidebarConfig {
  panels: string[];
  pinnedDocIds: string[];
  order: string[];
}

export const DEFAULT_SIDEBAR_CONFIG: SidebarConfig = {
  panels: ["music_player", "scene_map", "gm_pinned_file", "character_sheet"],
  pinnedDocIds: [],
  order: ["music_player", "scene_map", "gm_pinned_file", "character_sheet"],
};

interface CustomizeSidebarModalProps {
  isOpen: boolean;
  onClose: () => void;
  documents: readonly WorldDocument[];
  activeLocation?: (WorldDocument) | undefined;
  character?: (WorldDocument) | undefined;
  config: SidebarConfig;
  onSaveConfig: (config: SidebarConfig) => void;
}

const DEFAULT_PANEL_DEFS = [
  { id: "music_player", label: "เครื่องเล่นเพลง", icon: "🎵", desc: "ควบคุมเพลงและบรรยากาศเสียง" },
  { id: "scene_map", label: "แผนที่ฉาก", icon: "🗺", desc: "ภาพฉาก แผนที่ และสถานที่ปัจจุบัน" },
  { id: "gm_pinned_file", label: "เอกสารที่ GM ปักหมุด", icon: "📌", desc: "ภารกิจหรือเอกสารสำคัญของฉาก" },
  { id: "character_sheet", label: "การ์ดตัวละคร", icon: "👤", desc: "ข้อมูลตัวละครและสถานะปัจจุบัน" },
];

export function CustomizeSidebarModal({
  isOpen,
  onClose,
  documents,
  activeLocation,
  character,
  config,
  onSaveConfig,
}: CustomizeSidebarModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Derive categories from documents
  const categories = useMemo(() => {
    const set = new Set<string>();
    documents.forEach((doc) => {
      if (doc.kind) {
        set.add(doc.kind.charAt(0).toUpperCase() + doc.kind.slice(1));
      }
    });
    return ["All", ...Array.from(set).sort()];
  }, [documents]);

  // Filtered documents
  const filteredDocs = useMemo(() => {
    return documents.filter((doc) => {
      const matchesSearch =
        !searchQuery.trim() ||
        doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (doc.summary && doc.summary.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesCat =
        selectedCategory === "All" ||
        doc.kind?.toLowerCase() === selectedCategory.toLowerCase();

      return matchesSearch && matchesCat;
    });
  }, [documents, searchQuery, selectedCategory]);

  if (!isOpen) return null;

  const isPanelActive = (panelId: string) => config.panels.includes(panelId);
  const isDocPinned = (docId: string) => config.pinnedDocIds.includes(docId);

  const togglePanel = (panelId: string) => {
    let nextPanels: string[];
    let nextOrder: string[];

    if (isPanelActive(panelId)) {
      nextPanels = config.panels.filter((p) => p !== panelId);
      nextOrder = config.order.filter((id) => id !== panelId);
    } else {
      nextPanels = [...config.panels, panelId];
      nextOrder = [...config.order, panelId];
    }

    onSaveConfig({
      ...config,
      panels: nextPanels,
      order: nextOrder,
    });
  };

  const togglePinDoc = (docId: string) => {
    let nextDocIds: string[];
    let nextOrder: string[];

    if (isDocPinned(docId)) {
      nextDocIds = config.pinnedDocIds.filter((id) => id !== docId);
      nextOrder = config.order.filter((id) => id !== docId);
    } else {
      nextDocIds = [...config.pinnedDocIds, docId];
      nextOrder = [...config.order, docId];
    }

    onSaveConfig({
      ...config,
      pinnedDocIds: nextDocIds,
      order: nextOrder,
    });
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    const targetIdx = index + direction;
    if (targetIdx < 0 || targetIdx >= config.order.length) return;

    const newOrder = [...config.order];
    const [moved] = newOrder.splice(index, 1);
    if (moved === undefined) return;
    newOrder.splice(targetIdx, 0, moved);

    onSaveConfig({
      ...config,
      order: newOrder,
    });
  };

  const removeItem = (id: string) => {
    if (config.panels.includes(id)) {
      togglePanel(id);
    } else {
      togglePinDoc(id);
    }
  };

  const resetToDefault = () => {
    onSaveConfig(DEFAULT_SIDEBAR_CONFIG);
  };

  return (
    <div className="customize-sidebar-backdrop" onClick={onClose}>
      <div
        className="customize-sidebar-modal frosted-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Modal Header */}
        <header className="customize-modal-header">
          <div>
            <h2>ปรับแต่งแถบข้าง (Sidebar)</h2>
            <p>เลือกเปิดปิดเครื่องมือ และปักหมุดไฟล์ที่ต้องการไว้ที่แถบข้าง จัดลำดับได้ตามสะดวก</p>
          </div>
          <button
            type="button"
            className="customize-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </header>

        {/* Modal Body: Two Columns */}
        <div className="customize-modal-body">
          {/* Left Column: SIDEBAR PREVIEW */}
          <section className="sidebar-preview-column">
            <div className="preview-heading">
              <small>ตัวอย่างแถบข้าง</small>
              <button
                type="button"
                className="preview-reset-btn"
                onClick={resetToDefault}
              >
                ↺ รีเซ็ต
              </button>
            </div>

            <div className="preview-phone-card frosted-panel">
              {/* Fake top tabs bar */}
              <div className="preview-tabs-row">
                <div className="preview-tab-pill active">Overview</div>
                <div className="preview-tab-pill">Assistant</div>
                <div className="preview-tab-gear">⚙</div>
              </div>

              {/* Items in sidebar order */}
              <div className="preview-items-list">
                {config.order.map((itemId, idx) => {
                  const isPanel = DEFAULT_PANEL_DEFS.some((p) => p.id === itemId);
                  const panelDef = DEFAULT_PANEL_DEFS.find((p) => p.id === itemId);
                  const doc = !isPanel ? documents.find((d) => d.id === itemId) : null;

                  const title = isPanel
                    ? panelDef?.label.toUpperCase()
                    : doc?.title.toUpperCase() || itemId;
                  const desc = isPanel
                    ? panelDef?.desc
                    : doc?.summary || doc?.kind || "File";
                  const icon = isPanel ? panelDef?.icon : "📄";

                  return (
                    <div key={itemId} className="preview-item-card">
                      <div className="preview-item-main">
                        <div className="preview-item-icon">{icon}</div>
                        <div className="preview-item-info">
                          <strong>{title}</strong>
                          <small>
                            {itemId === "character_sheet" && character
                              ? character.title
                              : itemId === "gm_pinned_file" && activeLocation
                              ? activeLocation.title
                              : desc}
                          </small>
                        </div>
                      </div>

                      {/* Scene Map image preview in card */}
                      {itemId === "scene_map" && (activeLocation?.fields?.image || activeLocation?.fields?.imageUrl) && (
                        <div className="preview-map-thumb">
                          <AccountMediaImage
                            source={String(activeLocation.fields.image || activeLocation.fields.imageUrl)}
                            alt=""
                          />
                          <span>{activeLocation.title}</span>
                        </div>
                      )}

                      {/* Reorder & Remove Controls */}
                      <div className="preview-item-actions">
                        <button
                          type="button"
                          className="reorder-btn"
                          disabled={idx === 0}
                          onClick={() => moveItem(idx, -1)}
                          title="เลื่อนขึ้น"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="reorder-btn"
                          disabled={idx === config.order.length - 1}
                          onClick={() => moveItem(idx, 1)}
                          title="เลื่อนลง"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="reorder-btn danger"
                          onClick={() => removeItem(itemId)}
                          title="นำออก"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  );
                })}

                {config.order.length === 0 && (
                  <div className="preview-empty-notice">
                    <p>ยังไม่มีพาเนลที่เลือก กดเปิดที่ฝั่งขวาเพื่อเพิ่ม</p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Right Column: PANELS & FILES */}
          <section className="panels-and-files-column">
            {/* PANELS Section */}
            <div className="custom-section">
              <h3>เครื่องมือ (Panels)</h3>
              <div className="panels-pill-group">
                {DEFAULT_PANEL_DEFS.map((panel) => {
                  const active = isPanelActive(panel.id);
                  return (
                    <button
                      key={panel.id}
                      type="button"
                      className={`panel-toggle-pill ${active ? "active" : ""}`}
                      onClick={() => togglePanel(panel.id)}
                    >
                      <span className="toggle-check">{active ? "✓" : "+"}</span>
                      <span className="toggle-icon">{panel.icon}</span>
                      <span className="toggle-label">{panel.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* FILES Section */}
            <div className="custom-section">
              <div className="files-section-header">
                <h3>ไฟล์และเอกสาร</h3>
                <span className="files-pinned-count">
                  ปักหมุด {config.pinnedDocIds.length} ไฟล์
                </span>
              </div>

              {/* Search Bar */}
              <div className="files-search-box">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="ค้นหาตามชื่อไฟล์..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="clear-search-btn"
                    onClick={() => setSearchQuery("")}
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Category Pills Filter */}
              <div className="categories-filter-bar">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    className={`cat-pill ${selectedCategory === cat ? "active" : ""}`}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* Files Grid / List */}
              <div className="files-selection-grid">
                {filteredDocs.map((doc) => {
                  const pinned = isDocPinned(doc.id);
                  const img = typeof doc.fields?.image === "string" ? doc.fields.image : typeof doc.fields?.imageUrl === "string" ? doc.fields.imageUrl : "";

                  return (
                    <div
                      key={doc.id}
                      className={`file-select-card ${pinned ? "pinned" : ""}`}
                      onClick={() => togglePinDoc(doc.id)}
                    >
                      <div className="file-card-thumb">
                        {img ? (
                          <AccountMediaImage source={img} alt="" />
                        ) : (
                          <div className="file-icon-placeholder">
                            {doc.kind === "location"
                              ? "🗺"
                              : doc.kind === "item" || doc.kind === "weapon" || doc.kind === "armor"
                              ? "🗡"
                              : doc.kind === "character"
                              ? "👤"
                              : "📄"}
                          </div>
                        )}
                      </div>

                      <div className="file-card-info">
                        <strong>{doc.title}</strong>
                        <span className="file-kind-tag">{doc.kind?.toUpperCase() || "DOCUMENT"}</span>
                      </div>

                      <button
                        type="button"
                        className={`file-pin-action-btn ${pinned ? "active" : ""}`}
                        aria-label={pinned ? "ถอนหมุดไฟล์" : "ปักหมุดไฟล์"}
                      >
                        {pinned ? "✓" : "+"}
                      </button>
                    </div>
                  );
                })}

                {filteredDocs.length === 0 && (
                  <div className="no-files-found">
                    <p>ไม่พบไฟล์ที่ตรงกับคำค้นหา "{searchQuery}"</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>

        {/* Modal Footer */}
        <footer className="customize-modal-footer">
          <span className="footer-items-count">
            {config.order.length} รายการ
          </span>
          <button
            type="button"
            className="customize-done-btn"
            onClick={onClose}
          >
            เสร็จสิ้น
          </button>
        </footer>
      </div>
    </div>
  );
}
